import * as React from "react";
import styled from "styled-components";

import { Actions, ArrayLayoutProps, composePaths, ControlElement, Resolve } from "@jsonforms/core";

import { useJsonForms } from "@jsonforms/react";

import { mergeObjects, moveCard, moveToBottom, moveToTop, PlainObject } from "./utils";
import Layout from "./Layout";
import useStore, { arrayClipboardSelector, setArrayClipboardSelector } from "../../../store";
import { cloneDeep, isPlainObject } from "lodash";

const ErrorParagraph = styled.p`
	color: red;
`;

interface CustomArrayLayoutProps extends ArrayLayoutProps {
	uischema: {
		subText?: string;
		depth?: number;
		headingLevel?: number;
	} & ControlElement;
	subText?: string;
	showAccrordion?: boolean;
	headingLevel?: number;
}

export const CollapsibleArrayLayout: React.FC<CustomArrayLayoutProps> = ({
	uischema,
	schema,
	data,
	path,
	addItem,
	removeItems,
	enabled,
	moveDown,
	moveUp,
	showAccrordion = false,
	arraySchema,
	errors,
	rootSchema,
	label,
	id,
}) => {
	const ctx = useJsonForms();
	const arrayLabel: string | undefined = uischema?.options?.label || uischema?.label;
	const subText = uischema?.options?.subText || uischema?.subText;
	const elements = uischema.options?.["detail"] ? uischema.options?.["detail"] : [];
	const elementHeadingLevel = elements.headingLevel;
	const [focusIndex, setfocusIndex] = React.useState<string>("");
	const [dragFocusIndex, setDragFocusIndex] = React.useState<number | undefined>(undefined);
	const [isDraggingSelected, setIsDraggingSelected] = React.useState<boolean>(false);
	const [announcement, setAnnouncement] = React.useState<string>("");
	const [toastMessage, setToastMessage] = React.useState<string>("");

	const arrayClipboard = useStore(arrayClipboardSelector);

	const setArrayClipboard = useStore(setArrayClipboardSelector);

	const isEmpty = data === 0;
	const arrayDepth = path ? path.split(".").length : 0;
	const isChildArray = arrayDepth > 1;

	const addItemCb = React.useCallback(
		(p: string, value: any) => {
			return addItem(p, value);
		},
		[addItem]
	);

	const removeItemsCb = React.useCallback(
		(path: string, toDelete: number[]) => {
			return removeItems?.(path, toDelete) ?? (() => {});
		},
		[removeItems]
	);

	const onMoveUp = React.useCallback(
		(path: string, toMove: number) => {
			return moveUp && moveUp(path, toMove);
		},
		[moveUp]
	);

	const onMoveDown = React.useCallback(
		(path: string, toMove: number) => {
			return moveDown && moveDown(path, toMove);
		},
		[moveDown]
	);

	const onMoveToTop = React.useCallback(
		(currentIndex: number) => {
			return moveToTop({
				currentIndex,
				path,
				ctx,
				setAnnouncement,
			});
		},
		[ctx, path]
	);

	const onMoveToBottom = React.useCallback(
		(currentIndex: number) => {
			return moveToBottom({
				currentIndex,
				path,
				data,
				ctx,
				setAnnouncement,
			});
		},
		[ctx, data, path]
	);

	const onMoveCard = React.useCallback(
		(dragIndex: number, hoverIndex: number) => {
			moveCard({
				dragIndex,
				hoverIndex,
				path,
				ctx,
			});
			setAnnouncement(`Moved item from position ${dragIndex + 1} to position ${hoverIndex + 1}`);
		},
		[ctx, path]
	);

	const onCopyArrayValues = React.useCallback(
		(index: number) => {
			const currentData = Resolve.data(ctx.core?.data, path) ?? [];
			// Deep-clone only the single element at the given index
			setArrayClipboard(cloneDeep(currentData[index]));
			setAnnouncement(`Copied values from item ${index + 1}.`);
		},
		[ctx.core?.data, path, setArrayClipboard]
	);

	const onPasteArrayValues = React.useCallback(
		(index: number) => {
			if (arrayClipboard === null || arrayClipboard === undefined) return;

			const currentData: unknown[] = Resolve.data(ctx.core?.data, path) ?? [];
			const targetItem = currentData[index];
			const itemPath = composePaths(path, `${index}`);

			let nextValue: unknown;

			if (isPlainObject(targetItem) && isPlainObject(arrayClipboard)) {
				// Pass full arraySchema to handle nested properties recursively
				const mergeResult = mergeObjects(arrayClipboard as PlainObject, arraySchema);

				if (mergeResult.error) {
					const errorMessage =
						mergeResult.error === "missing-schema"
							? "Paste operation unsuccessful: array schema is not available."
							: "Paste operation unsuccessful: copied value does not match the target schema.";
					setAnnouncement(errorMessage);
					setToastMessage(errorMessage);
					return;
				}

				nextValue = mergeResult.value as PlainObject;
			} else {
				nextValue = cloneDeep(arrayClipboard);
			}

			ctx.dispatch?.(Actions.update(itemPath, () => nextValue));
			setAnnouncement(`Pasted values into item ${index + 1}.`);
		},
		[arrayClipboard, arraySchema, ctx, path, setAnnouncement]
	);

	if (!elements) {
		return <ErrorParagraph>No renderer found</ErrorParagraph>;
	}

	const accordionTitleHeadingLevel = elementHeadingLevel - 1;

	return (
		<Layout
			id={id}
			addItemCb={addItemCb}
			removeItemsCb={removeItemsCb}
			onMoveUp={onMoveUp}
			onMoveDown={onMoveDown}
			onMoveToTop={onMoveToTop}
			onMoveToBottom={onMoveToBottom}
			onMoveCard={onMoveCard}
			announcement={announcement}
			setAnnouncement={setAnnouncement}
			elements={elements}
			focusIndex={focusIndex}
			setfocusIndex={setfocusIndex}
			accordionTitleHeadingLevel={accordionTitleHeadingLevel}
			setDragFocusIndex={setDragFocusIndex}
			isDraggingSelected={isDraggingSelected}
			setIsDraggingSelected={setIsDraggingSelected}
			isChildArray={isChildArray}
			isEmpty={isEmpty}
			uischema={uischema}
			data={data}
			schema={schema}
			enabled={enabled}
			path={path}
			arraySchema={arraySchema}
			label={label}
			arrayLabel={arrayLabel || label}
			errors={errors}
			visible={false}
			addItem={addItem}
			rootSchema={rootSchema}
			dragFocusIndex={dragFocusIndex}
			subText={subText}
			showAccrordion={showAccrordion}
			onCopyArrayValues={onCopyArrayValues}
			onPasteArrayValues={onPasteArrayValues}
			canPaste={arrayClipboard !== null}
			toastMessage={toastMessage}
			setToastMessage={setToastMessage}
		/>
	);
};

export default CollapsibleArrayLayout;
